import { WrapperWidget } from '@agentselly/ui';
import { EmptyState } from '~/components/empty/EmptyState';
import { useTranslate } from '@tolgee/react';
import { NotFoundWidget } from './NotFoundWidget';
import { ForbiddenWidget } from '~/components/widget/base/ForbiddenWidget';
import { ErrorType } from '~/model/ErrorType';
import { BadRequestWidget } from '~/components/widget/base/BadRequestWidget';
import { Button } from '~/components/ui/Button';

export function ErrorWidget(props: Props) {
  const { t } = useTranslate();

  const errorMessage = props.error?.message || '';

  if (errorMessage === ErrorType.BadRequest) {
    return <BadRequestWidget />;
  }

  if (errorMessage === ErrorType.NotFound) {
    return <NotFoundWidget />;
  }

  if (errorMessage === ErrorType.Forbidden) {
    return <ForbiddenWidget />;
  }

  // Timeout / network failures of the /loader/ fetch: give a clear, honest
  // message and always offer a reload action (never a blank/white page).
  const isBackendTimeoutOrNetwork =
    errorMessage === ErrorType.BackendTimeout ||
    errorMessage === ErrorType.BackendNetwork;

  const title = isBackendTimeoutOrNetwork
    ? t('errorwidget_timeout_title') || 'Die Seite konnte nicht geladen werden'
    : t('errorwidget_title') || 'Hier ist etwas schiefgelaufen';

  const description = isBackendTimeoutOrNetwork
    ? t('errorwidget_timeout_description') ||
      'Der Server hat zu lange gebraucht zu antworten. Bitte lade die Seite erneut. Falls das Problem weiterhin auftritt, wende dich an den Support.'
    : t('errorwidget_description') ||
      'Die Seite konnte nicht geladen werden. Bitte versuche es erneut oder wende dich an den Support, falls das Problem weiterhin auftritt.';

  return (
    <WrapperWidget>
      <EmptyState
        icon={
          <svg
            xmlns="http://www.w3.org/2000/svg"
            fill="none"
            viewBox="0 0 24 24"
            strokeWidth={1.5}
            stroke="currentColor"
            className="h-6 w-6"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              d="M12 9v3.75m9-.75a9 9 0 1 1-18 0 9 9 0 0 1 18 0Zm-9 3.75h.008v.008H12v-.008Z"
            />
          </svg>
        }
        title={title}
        description={description}
        actions={
          props.onRetry ? (
            <Button
              primary
              isLoading={props.isRetrying}
              onClick={() => props.onRetry?.()}
            >
              {t('errorwidget_retry') || 'Seite neu laden'}
            </Button>
          ) : undefined
        }
      />
    </WrapperWidget>
  );
}

interface Props {
  readonly error: { message?: string } | null | undefined;
  readonly onRetry?: () => void;
  readonly isRetrying?: boolean;
}
